// External Libraries
import React from "react";
import Modal from "react-responsive-modal";
import * as Icon from "react-feather";

// Services
import CommunicationService from "../../../services/CommunicationService";
import UtilityService from "../../../services/UtilityService";

// Common Components
import WComponent from "../../common/WComponent";
import Button from "../../common/button/Button";
import TextFieldInput from "../../common/text_field_input/TextFieldInput";
import DateSelector from "../../common/date_selector/DateSelector";
import SearchableDropdown from "../../common/searchable_dropdown/SearchableDropdown";

// Styles
import "./communications.css";

const { alert } = UtilityService;

const DURATIONS = [
	{ id: 30,  name: "30 minutes" },
	{ id: 45,  name: "45 minutes" },
	{ id: 60,  name: "1 hour" },
	{ id: 90,  name: "1.5 hours" },
	{ id: 120, name: "2 hours" },
];

const initialState = () => ({
	saving: false,
	title: "",
	startDate: new Date(),
	durationMinutes: 60,
	selectedDurationName: "1 hour",
	description: "",
	scheduled: null,
	copied: false,
});

class ScheduleMeetingModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = initialState();
	}

	componentDidUpdate = prevProps => {
		if (prevProps.show === false && this.props.show === true) {
			this.update(initialState());
		}
	};

	onChange = e => {
		let { name, value } = e.target;
		this.update({ [name]: value });
	};

	onDurationSelect = item => {
		this.update({ durationMinutes: item.id, selectedDurationName: item.name });
	};

	onStartDateSelect = date => {
		this.update({ startDate: date });
	};

	onSchedule = async () => {
		let { clientId } = this.props;
		let { title, startDate, durationMinutes, description } = this.state;

		if (!title.trim() || !startDate) {
			alert({ type: "error", text: "Please fill in a title and select a date and time." });
			return;
		}

		await this.update({ saving: true });

		try {
			const result = await CommunicationService.scheduleMeeting({
				clientId,
				title: title.trim(),
				startAt: startDate.toISOString(),
				durationMinutes: parseInt(durationMinutes),
				description: description.trim() || undefined,
			});

			if (result) {
				await this.update({
					scheduled: {
						meetLink: result.meetLink || "",
						clientInvited: !!result.clientInvited,
					},
				});

				if (!result.clientInvited) {
					alert({ type: "info", text: "Meeting scheduled, but this client has no email on file — no invite was sent." });
				}
			} else {
				alert({ type: "error", text: "Could not schedule meeting. Make sure your Google account is connected." });
			}
		} catch (error) {
			console.log(error);
			alert({ type: "error", text: "Could not schedule meeting. Make sure your Google account is connected." });
		}

		await this.update({ saving: false });
	};

	onCopyLink = async () => {
		let { scheduled } = this.state;
		if (!scheduled || !scheduled.meetLink) return;

		const copied = await UtilityService.copyToClipboard({ text: scheduled.meetLink });
		if (!copied) {
			alert({ type: "error", text: "Could not copy the link. Please copy it manually." });
			return;
		}

		await this.update({ copied: true });
		setTimeout(() => this.update({ copied: false }), 2000);
	};

	renderSuccess() {
		let { onClose } = this.props;
		let { scheduled, copied } = this.state;

		return (
			<>
				<div className="comm-modal__body">
					<div className="schedule-modal__success">
						<div className="schedule-modal__success__icon">
							<Icon.CheckCircle size={48} strokeWidth={1.5} color="#1e8e3e" />
						</div>
						<div className="schedule-modal__success__title">Meeting scheduled</div>
						<div className="schedule-modal__success__sub">
							{scheduled.clientInvited
								? "A calendar invite has been emailed to the client."
								: "No email is on file for this client — no invite was sent."}
						</div>

						{scheduled.meetLink ? (
							<div className="schedule-modal__link-box">
								<div className="schedule-modal__link-box__url">{scheduled.meetLink}</div>
								<button
									className={`schedule-modal__link-box__copy${copied ? " schedule-modal__link-box__copy--done" : ""}`}
									onClick={this.onCopyLink}
								>
									{copied ? <Icon.Check size={14} /> : <Icon.Copy size={14} />}
									{copied ? "Copied" : "Copy link"}
								</button>
							</div>
						) : (
							<div className="schedule-modal__success__sub">
								No Google Meet link was returned for this event.
							</div>
						)}
					</div>
				</div>

				<div className="comm-modal__footer">
					<a
						className="schedule-modal__open-link"
						href={scheduled.meetLink}
						target="_blank"
						rel="noopener noreferrer"
					>
						<Icon.ExternalLink size={14} />
						Open in Google Meet
					</a>
					<Button text="Done" onClick={onClose} />
				</div>
			</>
		);
	}

	render() {
		let { show, onClose, clientName } = this.props;
		let { saving, title, startDate, selectedDurationName, description, scheduled } = this.state;

		return (
			<Modal open={show} onClose={onClose} classNames={{ modal: "comm-modal" }} center>
				<div className="comm-modal__header">
					<Icon.Video size={22} style={{ flexShrink: 0 }} />
					<div className="comm-modal__header__titles">
						<span className="comm-modal__header__title">Schedule Meeting</span>
						{clientName && (
							<span className="comm-modal__header__sub">with {clientName}</span>
						)}
					</div>
				</div>

				{scheduled ? this.renderSuccess() : (
					<>
						<div className="comm-modal__body">
							<TextFieldInput
								containerClass="schedule-modal__input"
								title="Meeting Title"
								name="title"
								value={title}
								onChange={this.onChange}
								autoComplete="off"
							/>

							<div className="schedule-modal__row">
								<DateSelector
									containerClass="schedule-modal__input"
									title="Date & Time"
									selectedDate={startDate}
									onDateSelect={this.onStartDateSelect}
									showTimeSelect
									dateFormat="MMM d, yyyy h:mm aa"
									timeIntervals={15}
								/>

								<SearchableDropdown
									containerClass="schedule-modal__input"
									title="Duration"
									options={DURATIONS}
									selectedName={selectedDurationName}
									onSelect={this.onDurationSelect}
									showAll
								/>
							</div>

							<TextFieldInput
								containerClass="schedule-modal__input"
								title="Description (optional)"
								name="description"
								value={description}
								onChange={this.onChange}
								autoComplete="off"
							/>

							<div className="schedule-modal__note">
								<Icon.Info size={13} />
								<span>This meeting and its transcript will be logged under this client only.</span>
							</div>
						</div>

						<div className="comm-modal__footer">
							<Button view="secondary" text="Cancel" onClick={onClose} disabled={saving} />
							<Button
								text={saving ? "Scheduling..." : "Schedule"}
								onClick={this.onSchedule}
								disabled={saving}
							/>
						</div>
					</>
				)}
			</Modal>
		);
	}
}

export default ScheduleMeetingModal;
